/**
 * Arc entity → `RenderableEntity` adapter.
 *
 * The DXF ARC stores `startAngle` / `endAngle` in **degrees**, with
 * angles measured CCW from +X. We convert to radians for the
 * `RenderableEntity.geometry` payload to keep the renderer math in a
 * single unit system.
 *
 * Bbox is computed by sampling the start/end points and any 0/90/180/
 * 270° points that fall inside the arc — same approach as
 * `entityBounds.ts` for parity.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import { readNumber, readPositiveNumber } from "../../scene/renderHelpers";
import type { RenderableEntity } from "../types";

import {
  buildRenderableShell,
  readCenterRaw,
  type AdapterContext,
} from "./adapterContext";
import { buildArcSceneGeometry } from "./sceneGeometryBuilders";

/** Adapter for DXF ARC / `Arc` entity type. */
export function arcToRenderable(
  entity: EntityPreview,
  ctx: AdapterContext,
): RenderableEntity {
  const centerRaw = readCenterRaw(entity.properties);
  const radius = readPositiveNumber(entity.properties, "Radius", 1);
  const startDeg = readNumber(entity.properties, "Start angle");
  const endDeg = readNumber(entity.properties, "End angle");

  const { geometry, bounds } = buildArcSceneGeometry(
    centerRaw,
    radius,
    startDeg,
    endDeg,
  );

  return buildRenderableShell(entity, ctx, geometry, bounds);
}
